import chunk from 'lodash/chunk'
import styled from 'styled-components'
import { Column, Row } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { Shelf, type ShelfViewAllProps } from '@/features/shop/components/Shelf'
import { useRenderShelfProducts } from '@/features/shop/components/Shelf/hooks/useRenderShelfProducts'
import { HERO_SHELF_ITEM_WIDTH, SHELF_ITEM_WIDTH } from '@/features/shared/constants/shelves'
import { ShelfViewAllButton } from '@/features/shop/components/Shelf/ShelfViewAllButton'
import { type Product } from '@/features/shop/services/Product/types'

type Props = {
  shelfIndex: number
  shelfName: string
  products: Product[]
  analyticSlug?: string
  viewAll?: ShelfViewAllProps
  linkToUniversalPDP: boolean
}

// Move this component's logic into HeroProductShelf or EnhancedProductShelf
// after the urchin to CMS migration is completed.
export const ChunkedProductsShelfRenderer = ({
  shelfIndex,
  shelfName,
  products,
  analyticSlug,
  viewAll,
  linkToUniversalPDP,
}: Props) => {
  return (
    <Shelf hideTitle shelfIndex={shelfIndex} title={shelfName} minHeight={264}>
      {chunk(products, 2).map((column) => (
        <ChunkedProducts
          key={column[0]?.id}
          products={column}
          shelfIndex={shelfIndex}
          shelfName={shelfName}
          analyticSlug={analyticSlug}
          viewAll={viewAll}
          linkToUniversalPDP={linkToUniversalPDP}
        />
      ))}
      {!!viewAll?.viewAllHref && (
        <ShelfViewAllButton
          aria-label={`View All ${shelfName}`}
          onClick={viewAll.onViewAll}
          href={viewAll.viewAllHref}
        />
      )}
    </Shelf>
  )
}

const ChunkedProducts = ({ ...props }: Props) => {
  const mobileShelfProducts = useRenderShelfProducts({
    ...props,
    wrapper: ProductCardWrapper,
  })
  const tabletShelfProducts = useRenderShelfProducts({
    ...props,
    landscape: true,
    wrapper: LandscapeProductCardWrapper,
  })
  return (
    <>
      <MobileWrapper>{mobileShelfProducts}</MobileWrapper>
      <DesktopWrapper>{tabletShelfProducts}</DesktopWrapper>
    </>
  )
}

const MobileWrapper = styled(Row)`
  @media ${screenSizes.tablet} {
    display: none;
  }
`

const DesktopWrapper = styled(Column)`
  display: none;

  @media ${screenSizes.tablet} {
    display: initial;
  }
`

const LandscapeProductCardWrapper = styled.div`
  &:first-child {
    margin-bottom: 1.5rem;
  }

  width: ${HERO_SHELF_ITEM_WIDTH}px;
`

const ProductCardWrapper = styled.div`
  width: ${SHELF_ITEM_WIDTH}px;

  @media ${screenSizes.max_mobile} {
    &:first-child {
      margin-right: 0.5rem;
    }
  }
`
